import React from 'react';
import {
  ArrowPathIcon,
  KeyIcon,
  TrashIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { ActionIconButton } from '../ui/AdminPrimitives';
import { RoleBadge, UserActiveBadge } from './PlatformBadges';

export interface TenantUserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLogin: string | null;
}

interface TenantUsersTableProps {
  users: TenantUserRow[];
  onToggleStatus: (userId: string) => void;
  onResetPassword: (userId: string) => void;
  onDelete: (user: TenantUserRow) => void;
  togglePending?: boolean;
}

const formatLastLogin = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

export const TenantUsersTable: React.FC<TenantUsersTableProps> = ({
  users,
  onToggleStatus,
  onResetPassword,
  onDelete,
  togglePending,
}) => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-4 py-3">
      <div className="flex items-center gap-2">
        <EllipsisHorizontalIcon className="h-4 w-4 text-slate-400" aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Usuarios del espacio
        </span>
      </div>
      <span className="text-xs text-slate-400">{users.length} cuenta{users.length !== 1 ? 's' : ''}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="whitespace-nowrap px-4 py-3 font-medium">Usuario</th>
            <th className="whitespace-nowrap px-4 py-3 font-medium">Email</th>
            <th className="whitespace-nowrap px-4 py-3 font-medium">Rol</th>
            <th className="whitespace-nowrap px-4 py-3 font-medium">Estado</th>
            <th className="hidden whitespace-nowrap px-4 py-3 font-medium md:table-cell">Último acceso</th>
            <th className="whitespace-nowrap px-4 py-3 text-right font-medium">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {users.map((u) => (
            <tr key={u.id} className="transition-colors hover:bg-slate-50/80">
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-slate-900">{u.name}</span>
                  {u.role === 'ADMIN' && (
                    <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-600">
                      Cuenta principal
                    </span>
                  )}
                </div>
              </td>
              <td className="max-w-[200px] truncate px-4 py-3 font-mono text-xs text-slate-600" title={u.email}>
                {u.email}
              </td>
              <td className="px-4 py-3">
                <RoleBadge role={u.role} />
              </td>
              <td className="px-4 py-3">
                <UserActiveBadge isActive={u.isActive} />
              </td>
              <td className="hidden px-4 py-3 text-xs text-slate-500 md:table-cell">
                {formatLastLogin(u.lastLogin)}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-0.5">
                  <ActionIconButton
                    title={u.isActive ? 'Desactivar acceso' : 'Activar acceso'}
                    onClick={() => onToggleStatus(u.id)}
                    disabled={togglePending}
                    tone="default"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                  </ActionIconButton>
                  <ActionIconButton
                    title="Establecer nueva contraseña"
                    onClick={() => onResetPassword(u.id)}
                    tone="info"
                  >
                    <KeyIcon className="h-4 w-4" />
                  </ActionIconButton>
                  <ActionIconButton
                    title="Eliminar usuario"
                    onClick={() => onDelete(u)}
                    tone="danger"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </ActionIconButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
